import { useMotionValueEvent, useScroll } from 'framer-motion';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import RoundArrowSVG from 'public/icons/round-arrow.svg';
import { useRef, useState } from 'react';
import type { Thing } from 'schema-dts';
import { PAGES } from '@/consts';
import { Day } from '@/entities/day';
import { Direction } from '@/entities/direction';
import { Format } from '@/entities/format';
import { Place } from '@/entities/place';
import { getCommonPageProps } from '@/shared/api';
import { getDays } from '@/shared/api/days';
import { getDirections } from '@/shared/api/directions';
import { getPageDetail } from '@/shared/api/page-detail';
import { getFormats } from '@/shared/api/participation-formats';
import { getPlaces } from '@/shared/api/places';
import { ApiProgramRequest, AppProgramResponse, getProgram } from '@/shared/api/program';
import { getSiteSettings } from '@/shared/api/site-settings';
import { useHeaderThemeValue } from '@/shared/atoms/header-theme';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import SubscribePopup from '@/shared/components/popups/SubscribePopup';
import ProgramDetailSection from '@/shared/components/sections/ProgramDetailSection';
import { ProgramDetailSectionProps } from '@/shared/components/sections/ProgramDetailSection/ProgramDetailSection';
import Breadcrumbs from '@/shared/components/shared/Breadcrumbs';
import DownloadPdfBtn from '@/shared/components/shared/DownloadPdfBtn/DownloadPdfBtn';
import JsonLd from '@/shared/components/shared/JsonLd/JsonLd';
import Link from '@/shared/components/shared/Link';
import ScrollTopButton from '@/shared/components/shared/ScrollTopButton';
import Tooltip from '@/shared/components/shared/Tooltip';
import Typografed from '@/shared/components/shared/Typografed';
import { CommonPageProps, NonUndefined } from '@/shared/lib/types';
import { generateProgramEventJsonLd } from '@/shared/lib/utils/event-json-ld';
import { splitSubtitlePreservingRest } from '@/shared/lib/utils/split-subtitle-preserving-rest';

const ProgramPage = ({
    h1,
    subtitle,
    breadcrumbs,
    footerData,
    programDetailSectionProps,
    places,
    days,
    formats,
    directions,
    initialParams = {},
    initialProgramResponse,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const ref = useRef(null);
    const [isButtonVisible, setIsButtonVisible] = useState(false);
    const theme = useHeaderThemeValue();
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });

    useMotionValueEvent(scrollYProgress, 'change', (progress) => {
        setIsButtonVisible(progress > 0);
    });

    const [part1, part2] = splitSubtitlePreservingRest(subtitle);

    return (
        <DefaultLayout currentPageId={PAGES.PROGRAM} ref={ref}>
            <div className="news-page-top js-header-theme-trigger wrapper" data-theme="dark" id="top">
                <Breadcrumbs className="news-page-breadcrumbs" breadcrumbs={breadcrumbs} />
                <div className="program-detail-title-block">
                    <div className="program-detail-title-block-text">
                        {subtitle && part1 && part2 && (
                            <div className="program-main-article text-m">
                                <span>{part1}</span>
                                <span>{part2}</span>
                            </div>
                        )}
                        <h1 className="program-detail-title h2">
                            <Typografed>{h1}</Typografed>
                        </h1>
                    </div>
                    <div className="program-detail-title-btns">
                        <Tooltip message="Тур по TNF 2024">
                            <Link href="/tour-tnf/#tour" className="program-detail-360-btn text-xs">
                                360°
                                <RoundArrowSVG className="round-arrow-svg" />
                            </Link>
                        </Tooltip>

                        <DownloadPdfBtn />
                    </div>
                </div>
            </div>

            <ProgramDetailSection
                reflectFilterStateInUrl
                programDetailSectionProps={programDetailSectionProps}
                isButtonVisible={isButtonVisible}
                days={days}
                directions={directions}
                places={places}
                formats={formats}
                initialParams={initialParams}
                initialResponse={initialProgramResponse}
                mainTheme={h1}
                pdfTitle="Главная тема форума"
            />
            <SubscribePopup />
            <ScrollTopButton theme={theme} scrollYProgress={scrollYProgress} universal />
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-program-2025"
                data={generateProgramEventJsonLd({
                    h1,
                    subtitle,
                    events: initialProgramResponse.data?.items || [],
                    days,
                    places,
                    pagePath: '/program-2025/',
                    breadcrumbs,
                    contacts: footerData,
                })}
            />
        </DefaultLayout>
    );
};

export default ProgramPage;

type ProgramPageProps = NonUndefined<
    CommonPageProps & {
        h1: string;
        subtitle: string;
        programDetailSectionProps: ProgramDetailSectionProps;
        days: Day[];
        formats: Format[];
        places: Place[];
        directions: Direction[];
        initialParams: ApiProgramRequest;
        initialProgramResponse: AppProgramResponse;
    }
>;

export const getServerSideProps: GetServerSideProps<ProgramPageProps> = async ({ query }) => {
    const pageResponse = await getPageDetail({ params: { slug: 'program' } });
    const initialParams: ApiProgramRequest = {};
    const [
        commonPageProps,
        [days, programDetailResponse],
        placesResponse,
        directionsResponse,
        formatsResponse,
        siteSettings,
    ] = await Promise.all([
        getCommonPageProps(pageResponse.data?.id ?? PAGES.PROGRAM),
        getDays({ params: { page: pageResponse.data?.id ?? PAGES.PROGRAM } }).then((daysResponse) => {
            const days = daysResponse.data || [];
            const dayIds = days.map((day) => day.id);

            if (typeof query.day === 'string') {
                initialParams.day = parseInt(query.day);
            }

            if (typeof query.directions === 'string') {
                initialParams.directions = query.directions.split(',').map((id) => parseInt(id.trim()));
            }

            if (typeof query.q === 'string') {
                initialParams.q = query.q;
            }

            return Promise.all([
                days,
                getProgram({
                    params: {
                        ...initialParams,
                        days: typeof initialParams.day === 'number' ? [initialParams.day] : days.map((day) => day.id),
                    },
                }),
                getProgram({
                    params: {
                        days: dayIds,
                        is_free: 1,
                    },
                }),
            ]);
        }),
        getPlaces(),
        getDirections(),
        getFormats({ params: { page: pageResponse.data?.id ?? PAGES.PROGRAM } }),
        getSiteSettings({
            params: {
                keys: ['program_first_screen_title', 'program_first_screen_subtitle'],
            },
        }),
    ]);

    const formats = formatsResponse.data || [];
    const directions = directionsResponse.data || [];
    const places = placesResponse.data || [];
    return {
        props: {
            ...commonPageProps,
            bodyClass: 'program-page',
            meta: {
                ...commonPageProps.meta,
                ...(pageResponse.data?.meta ?? []),
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: 'Программа 2025', href: '/program-2025/' },
            ],
            h1: siteSettings.data?.program_first_screen_title || 'Технологическая устойчивость: стратегия развития',
            subtitle: siteSettings.data?.program_first_screen_subtitle || 'Главная тема форума',
            programDetailSectionProps: {
                cards: programDetailResponse.data?.items || [],
            },
            directions,
            formats,
            days,
            places,
            initialParams,
            initialProgramResponse: programDetailResponse,
        },
    };
};
